import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { OrderDetailResponse } from './useGetOrderDetail';

export type ManualFlagType =
    | 'head-tail-lamps-with-logo'
    | 'oe-warranty'
    | 'welded-on-parts'
    | 'exclude-lkq-parts'
    | 'shop-loss'
    | 'promo-pricing'
    | 'request-oem-one-time-use-part';

interface TogglePartFlagInput {
    orderPartId: string;
    orderId: string;
    flagReason?: string;
    manualFlagType?: ManualFlagType;
}

const togglePartFlag = async (data: TogglePartFlagInput, sessionId: string | null) => {
    return axios.post(`${API_URL}/order/part/flag`, {
        orderPartId: data.orderPartId,
        flagReason: data.flagReason,
        manualFlagType: data.manualFlagType
    }, {
        headers: {
            Authorization: sessionId
        }
    });
};

export const useTogglePartFlag = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: TogglePartFlagInput) => togglePartFlag(data, sessionContext?.sessionId ?? null),
        onMutate: async (newTodo) => {
            await queryClient.cancelQueries({ queryKey: ['/order/detail', newTodo.orderId] });
            await queryClient.invalidateQueries({ queryKey: ['quoteData', newTodo.orderId] });

            const previousOrder = queryClient.getQueryData<OrderDetailResponse>(['/order/detail', newTodo.orderId]);

            if (previousOrder) {
                queryClient.setQueryData<OrderDetailResponse>(['/order/detail', newTodo.orderId], (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        newParts: {
                            parts: old.newParts.parts.map(part =>
                                part._id === newTodo.orderPartId
                                    ? { ...part, flag: !part.flag }
                                    : part
                            ),
                        }
                    };
                });
            }
        },
    });
};

